import { assert, ignore, isEmptyOrUndefinedish, panic } from "common/Support";
import { usePreviousValue } from "hooks/use-previous-prop";
import type { DependencyList } from "react";
import { createContext, useContext, useEffect, useRef } from "react";
import type { GroupHeader, ListGroup } from "./list-types";

const ItemRowIndexContext = createContext(0);

export const ItemRowIndexContextProvider = ItemRowIndexContext.Provider;

const ListCacheClearingContext = createContext<(rowIndex: number) => void>(ignore);

export const ListCacheClearingContextProvider = ListCacheClearingContext.Provider;

/**
 * Notifies the parent List that it should measure rows again whenever _some_ dependency changes.
 *
 * @param deps Dependencies that should make the list measure rows again
 */
export function useListItemSizeChange(...deps: DependencyList) {
    // We don't want to notify size changes on first renders.
    const isMountRender = useRef(true);
    const notifySizeChange = useContext(ListCacheClearingContext);
    const rowIndex = useContext(ItemRowIndexContext);

    useEffect(() => {
        if (isMountRender.current) {
            isMountRender.current = false;
            return;
        }

        notifySizeChange(rowIndex);

        // Disabling exhaustive-deps cause we want to notify whenever deps change
        // even though we don't directly use them.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowIndex, notifySizeChange, ...deps]);
}

/**
 * Returns whether the value from the previous and current call went from being
 * defined to undefined or viceversa
 */
export function useChangeEmptyness(value: string | undefined) {
    const previousValue = usePreviousValue(value);

    return isEmptyOrUndefinedish(previousValue) !== isEmptyOrUndefinedish(value);
}

export function isGroupHeader<T>(item: T | GroupHeader): item is GroupHeader {
    return (item as any).isGroupHeader === true;
}

export class ListProxy<T> {
    constructor(private items: readonly T[] | undefined, private groups: readonly ListGroup<T>[] | undefined) {
        assert(this.groups !== undefined || this.items !== undefined);
    }

    public get(index: number): T | GroupHeader {
        if (this.items !== undefined) {
            return this.items[index];
        }

        assert(this.groups !== undefined);

        for (const g of this.groups) {
            if (index === 0) {
                return {
                    isGroupHeader: true,
                    groupHeaderName: g.title,
                    onSeeAll: g.onSeeAll,
                };
            }
            index--;

            if (index < g.items.length) {
                return g.items[index];
            }

            index -= g.items.length;
        }

        panic("Groups or Items must be valid.");
    }

    public get length(): number {
        if (this.items !== undefined) {
            return this.items.length;
        }

        assert(this.groups !== undefined);

        return this.groups.map(g => g.items.length + 1).reduce((pv, cv) => pv + cv, 0);
    }
}
